import {
    Text,
    Container,
    Breakpoints,
    TextTag,
    TokenTextAppearance,
    TokenTextColor,
} from '@volkswagen-onehub/components-core'
import {SizeEnum} from '@volkswagen-onehub/tokens'
import styled from 'styled-components'
import {
    CertifiedRetailer,
    CertifiedRepair,
    Safety,
    BusinessCustomersPrivate,
    DriverAssistance,
} from '@volkswagen-onehub/icons-core'
import React, {useEffect, useState} from 'react'
import {Trans, useTranslation} from 'react-i18next'
import {Global} from '../../shared/variables'
import {useSelector} from 'react-redux'
import {RootState} from '../rootReducer'
import isEmpty from 'lodash.isempty'
// tslint:disable-next-line:no-implicit-dependencies no-submodule-imports
import type {DealerConfigurationV1} from '@volkswagen-onehub/mydealer-configuration-service/lib/v1'

const StageContainer = styled.div`
    padding-top: ${(props) => props.theme.size.static650};
    padding-bottom: ${(props) => props.theme.size.dynamic0150};
    padding-right: ${(props) => props.theme.size.grid002};
    padding-left: ${(props) => props.theme.size.grid002};
    background: ${({theme}) => theme.colors.gradient.left};

    @media (min-width: ${Breakpoints.b560}px) {
        padding-right: ${(props) => props.theme.size.grid001};
        padding-left: ${(props) => props.theme.size.grid001};
    }

    @media (min-width: ${Breakpoints.b1280}px) {
        padding-right: ${(props) => props.theme.size.grid002};
        padding-left: ${(props) => props.theme.size.grid002};
    }

    @media (min-width: ${Breakpoints.b2560}px) {
        padding-left: ${(props) => props.theme.size.grid006};
        padding-right: ${(props) => props.theme.size.grid006};
    }
`

const ItemContainer = styled.div`
    display: flex;
    width: 100%;
    justify-content: start;
    gap: ${(props) => props.theme.size.static200};
    margin-bottom: ${(props) => props.theme.size.static200};
`

const EyeCatcherBg = styled.div`
    color: ${({theme}) => theme.colors.content.inverted};
    background-color: ${({theme}) => theme.colors.signal.neutral};
    width: 100%;
    padding: ${(props) => props.theme.size.static250}
        ${(props) => props.theme.size.grid001};
    font-size: ${({theme}) => theme.fonts.copy[200].fontSize};
    line-height: ${({theme}) => theme.fonts.copy[200].lineHeight};
    display: inline-flex;
    align-items: center;
    margin-top: var(
        --s2-one-hub-navigation-top-bar-height,
        ${({theme}) => theme.size.static650}
    );

    p {
        margin: 0;
    }

    @media (max-width: ${Breakpoints.b560}px) {
        padding-top: ${(props) => props.theme.size.static500};
    }
`

const StyledTextTitle = styled.h1`
    color: ${({theme}) => theme.colors.content.inverted};
    font-size: ${({theme}) => theme.fonts.headline[500].fontSize};
    line-height: ${({theme}) => theme.fonts.headline[500].lineHeight};
    margin: 0;

    span {
        font-weight: 100;
    }
`
export function HomeStage(): JSX.Element {
    const {t} = useTranslation('fa-flotte')

    const [stageText, setStageText] = useState([])
    const [isFleetDealerInSpecialization, setIsFleetDealerInSpecialization] =
        useState(true)
    const serviceType = useSelector(
        (state: RootState) => state.general.serviceType
    )
    const dealers = useSelector(
        (state: RootState) => state.dealerFormData.dealersOptions
    )
    const myDealerCode = useSelector(
        (state: RootState) => state.dealer.myDealerCode
    )

    const isFleet = serviceType === Global.FLEET
    const isPrivate = serviceType === Global.PRIVATE
    const isLeasing = serviceType === Global.LEASING

    const simulationsList = useSelector(
        (state: RootState) => state.configurationModelsData.simulationsList
    )

    useEffect(() => {
        setStageText(
            t(serviceType + '.stage.text.standard', {returnObjects: true})
        )
    }, [])

    useEffect(() => {
        if (myDealerCode && isFleet && dealers.length > 0) {
            const dealer = dealers.find(
                (d: {[id: string]: DealerConfigurationV1}) =>
                    d.bearer_code === myDealerCode
            )
            const hasFlotteVendita = dealer?.specilization?.includes(
                Global.FLOTTE_SPECIALIZATION
            )

            setIsFleetDealerInSpecialization(hasFlotteVendita || false)
        } else if (!myDealerCode) {
            setIsFleetDealerInSpecialization(true)
        }
    }, [myDealerCode, dealers])

    return (
        <>
            {!isFleetDealerInSpecialization ? (
                <EyeCatcherBg
                    dangerouslySetInnerHTML={{
                        __html: t(
                            serviceType + '.stage.dealer.notInSpecialization'
                        ),
                    }}
                />
            ) : undefined}
            <StageContainer
                className={
                    isFleetDealerInSpecialization ? '' : 'eye-catcher-padding'
                }
            >
                <StyledTextTitle>
                    <Trans
                        i18nKey={serviceType + '.stage.title'}
                        ns="fa-flotte"
                        components={{span: <span />}}
                    />
                </StyledTextTitle>

                <Container
                    padding={{
                        top: {
                            [Breakpoints.default]: SizeEnum.static450,
                        },
                        right: {
                            [Breakpoints.b960]: SizeEnum.dynamic0300,
                        },
                        left: {
                            [Breakpoints.b960]: SizeEnum.dynamic0300,
                        },
                    }}
                    horizontalAlign="flex-start"
                    gutter={SizeEnum.static300}
                    wrap="always"
                >
                    <Container
                        padding={{
                            top: {
                                [Breakpoints.default]: SizeEnum.static150,
                            },
                            right: {
                                [Breakpoints.b1280]: SizeEnum.dynamic0300,
                            },
                            left: {
                                [Breakpoints.b1280]: SizeEnum.dynamic0300,
                            },
                        }}
                        horizontalAlign="flex-start"
                        gutter={SizeEnum.static300}
                        wrap="always"
                    >
                        <Text
                            color={TokenTextColor.inverted}
                            bold
                            tag={TextTag.h2}
                            appearance={TokenTextAppearance.headline300}
                        >
                            {t(serviceType + '.stage.subtitle')}
                        </Text>

                        {stageText.map((paragraph, index) => (
                            <Text
                                key={index}
                                color={TokenTextColor.inverted}
                                tag={TextTag.p}
                                appearance={TokenTextAppearance.copy200}
                            >
                                <span
                                    dangerouslySetInnerHTML={{
                                        __html: t(paragraph),
                                    }}
                                />
                            </Text>
                        ))}

                        {isEmpty(simulationsList) && (
                            <Text
                                color={TokenTextColor.inverted}
                                tag={TextTag.p}
                                appearance={TokenTextAppearance.copy200}
                            >
                                <span
                                    dangerouslySetInnerHTML={{
                                        __html: t(
                                            serviceType + '.stage.text.expired'
                                        ),
                                    }}
                                />
                            </Text>
                        )}

                        {!isLeasing && (
                            <Text
                                color={TokenTextColor.inverted}
                                tag={TextTag.strong}
                                bold
                                appearance={TokenTextAppearance.copy200}
                            >
                                <span
                                    dangerouslySetInnerHTML={{
                                        __html: t(
                                            serviceType + '.stage.list-title'
                                        ),
                                    }}
                                />
                            </Text>
                        )}
                    </Container>

                    {!isLeasing && (
                        <Container
                            padding={{
                                right: {
                                    [Breakpoints.b1280]: SizeEnum.dynamic0300,
                                    [Breakpoints.b2560]: SizeEnum.dynamic0450,
                                },
                                left: {
                                    [Breakpoints.b1280]: SizeEnum.dynamic0300,
                                    [Breakpoints.b2560]: SizeEnum.dynamic0450,
                                },
                            }}
                            horizontalAlign="flex-start"
                            gutter={SizeEnum.static250}
                            wrap="always"
                        >
                            {isFleet && (
                                <>
                                    <ItemContainer>
                                        <Text color={TokenTextColor.inverted}>
                                            <CertifiedRetailer aria-hidden />
                                        </Text>
                                        <Text
                                            color={TokenTextColor.inverted}
                                            tag={TextTag.p}
                                            appearance={
                                                TokenTextAppearance.copy200
                                            }
                                        >
                                            {t(
                                                serviceType +
                                                    '.stage.list-items.1'
                                            )}
                                        </Text>
                                    </ItemContainer>
                                    <ItemContainer>
                                        <Text color={TokenTextColor.inverted}>
                                            <CertifiedRepair aria-hidden />
                                        </Text>
                                        <Text
                                            color={TokenTextColor.inverted}
                                            tag={TextTag.p}
                                            appearance={
                                                TokenTextAppearance.copy200
                                            }
                                        >
                                            {t(
                                                serviceType +
                                                    '.stage.list-items.2'
                                            )}
                                        </Text>
                                    </ItemContainer>
                                    <ItemContainer>
                                        <Text color={TokenTextColor.inverted}>
                                            <Safety aria-hidden />
                                        </Text>
                                        <Text
                                            color={TokenTextColor.inverted}
                                            tag={TextTag.p}
                                            appearance={
                                                TokenTextAppearance.copy200
                                            }
                                        >
                                            {t(
                                                serviceType +
                                                    '.stage.list-items.3'
                                            )}
                                        </Text>
                                    </ItemContainer>
                                    <ItemContainer>
                                        <Text color={TokenTextColor.inverted}>
                                            <BusinessCustomersPrivate
                                                aria-hidden
                                            />
                                        </Text>
                                        <Text
                                            color={TokenTextColor.inverted}
                                            tag={TextTag.p}
                                            appearance={
                                                TokenTextAppearance.copy200
                                            }
                                        >
                                            {t(
                                                serviceType +
                                                    '.stage.list-items.4'
                                            )}
                                        </Text>
                                    </ItemContainer>
                                </>
                            )}
                            {isPrivate && (
                                <>
                                    <ItemContainer>
                                        <Text color={TokenTextColor.inverted}>
                                            <Safety aria-hidden />
                                        </Text>
                                        <Text
                                            color={TokenTextColor.inverted}
                                            tag={TextTag.p}
                                            appearance={
                                                TokenTextAppearance.copy200
                                            }
                                        >
                                            {t(
                                                serviceType +
                                                    '.stage.list-items.1'
                                            )}
                                        </Text>
                                    </ItemContainer>
                                    <ItemContainer>
                                        <Text color={TokenTextColor.inverted}>
                                            <CertifiedRetailer aria-hidden />
                                        </Text>
                                        <Text
                                            color={TokenTextColor.inverted}
                                            tag={TextTag.p}
                                            appearance={
                                                TokenTextAppearance.copy200
                                            }
                                        >
                                            {t(
                                                serviceType +
                                                    '.stage.list-items.2'
                                            )}
                                        </Text>
                                    </ItemContainer>
                                    <ItemContainer>
                                        <Text color={TokenTextColor.inverted}>
                                            <CertifiedRepair aria-hidden />
                                        </Text>
                                        <Text
                                            color={TokenTextColor.inverted}
                                            tag={TextTag.p}
                                            appearance={
                                                TokenTextAppearance.copy200
                                            }
                                        >
                                            {t(
                                                serviceType +
                                                    '.stage.list-items.3'
                                            )}
                                        </Text>
                                    </ItemContainer>
                                    <ItemContainer>
                                        <Text color={TokenTextColor.inverted}>
                                            <DriverAssistance aria-hidden />
                                        </Text>
                                        <Text
                                            color={TokenTextColor.inverted}
                                            tag={TextTag.p}
                                            appearance={
                                                TokenTextAppearance.copy200
                                            }
                                        >
                                            {t(
                                                serviceType +
                                                    '.stage.list-items.4'
                                            )}
                                        </Text>
                                    </ItemContainer>
                                </>
                            )}
                        </Container>
                    )}
                </Container>
            </StageContainer>
        </>
    )
}
